import { Image } from 'expo-image';
import { View, ViewProps } from 'react-native';

import type { PageBuilderWidgetMarketingTileRow } from '@fhs/cms-groq-queries';
import { imageUrlBuilder } from '@fhs/cms-groq-queries';
import { Text, YStack, createMqStyles, tokens, useMqSelect } from '@fhs/ui';

import { AppStoreButton } from '../components/app-store-button';
import { CtaButton } from '../components/cta-button';
import { SimpleBlockContent } from '../components/simple-block-content';
import { ActionHandler } from '../types';
import { addActionSource } from '../utils/action';

export type WidgetMarketingTileRowProps = Pick<
  PageBuilderWidgetMarketingTileRow,
  | 'mediaLayoutDesktop'
  | 'mediaType'
  | 'image'
  | 'prefixType'
  | 'prefixImage'
  | 'prefixText'
  | 'title'
  | 'description'
  | 'buttons'
> & {
  onAction: ActionHandler;
  style?: ViewProps['style'];
};

export function WidgetMarketingTileRow(props: WidgetMarketingTileRowProps) {
  const mqStyles = useMqStyles();
  const isLtDesktop = useMqSelect({ $gteDesktop: false }, true);
  const isGteDesktop = useMqSelect({ $gteDesktopLg: true }, false);
  const mediaFirst = isLtDesktop || props.mediaLayoutDesktop === 'left';
  const mediaAspectRatio = useMqSelect({ $gteDesktop: '131 / 119' }, '39/25');

  const media = props.image ? (
    <Image
      source={imageUrlBuilder.image(props.image).format('webp').toString()}
      style={[mqStyles.image, { aspectRatio: mediaAspectRatio }]}
    />
  ) : null;

  return (
    <View style={[mqStyles.container, props.style]}>
      <View style={mqStyles.card}>
        {mediaFirst && media}
        <YStack style={mqStyles.content}>
          <YStack style={mqStyles.textContentArea}>
            {(() => {
              switch (props.prefixType) {
                case 'none': {
                  return null;
                }

                case 'image': {
                  if (!props.prefixImage) {
                    return null;
                  }

                  return (
                    <Image
                      source={imageUrlBuilder.image(props.prefixImage).format('webp').toString()}
                      alt={props.prefixText ?? ''}
                      style={mqStyles.prefixImage}
                      contentFit="contain"
                      contentPosition="left"
                    />
                  );
                }

                case 'text': {
                  return (
                    <Text.Paragraph size="md" style={mqStyles.prefixText}>
                      {props.prefixText}
                    </Text.Paragraph>
                  );
                }
              }
            })()}

            {props.title && (
              <SimpleBlockContent
                value={props.title}
                onAction={addActionSource(
                  { component: 'title', componentType: 'block' },
                  props.onAction
                )}
                normalTextType="heading"
                normalText={{
                  type: isGteDesktop || isLtDesktop ? 'hero' : 'one',
                  style: { color: tokens.colors.$houseDark, textAlign: 'center' },
                }}
              />
            )}

            {props.description && (
              <SimpleBlockContent
                value={props.description}
                onAction={addActionSource(
                  { component: 'description', componentType: 'block' },
                  props.onAction
                )}
                normalText={{
                  size: 'md',
                  style: { color: tokens.colors.$houseDark, textAlign: 'center' },
                }}
              />
            )}
          </YStack>

          <View style={mqStyles.buttons}>
            {(props.buttons ?? []).map(btn => {
              switch (btn._type) {
                case 'pageBuilderButtonCta': {
                  return (
                    <CtaButton
                      key={btn._key}
                      onAction={addActionSource(
                        { component: 'CTA', componentType: 'button', text: btn.text },
                        props.onAction
                      )}
                      size="lg"
                      variant={btn.variant}
                      leadingIcon={btn.leadingIcon}
                      link={btn.link}
                      trailingIcon={btn.trailingIcon}
                      text={btn.text}
                    />
                  );
                }

                case 'pageBuilderButtonStore': {
                  return (
                    <AppStoreButton
                      key={btn._key}
                      platform={btn.platform}
                      onAction={addActionSource(
                        {
                          component: 'App Store',
                          componentType: 'button',
                          text: btn.platform,
                        },
                        props.onAction
                      )}
                    />
                  );
                }

                default: {
                  return null;
                }
              }
            })}
          </View>
        </YStack>
        {!mediaFirst && media}
      </View>
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      paddingHorizontal: 16,
      gap: 24,
    },
    $gteDesktop: {
      paddingHorizontal: 32,
      gap: 32,
      width: '100%',
      maxWidth: 1400,
      marginHorizontal: 'auto',
    },
  },
  card: {
    $base: {
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: tokens.colors.$heritageWhite,
    },
    $gteDesktop: {
      flexDirection: 'row',
    },
  },
  image: {
    $base: {
      width: '100%',
    },
    $gteDesktop: {
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: '50%',
    },
  },
  content: {
    $base: {
      paddingVertical: 24,
      paddingHorizontal: 20,
      gap: 24,
    },
    $gteDesktop: {
      height: '100%',
      aspectRatio: '131 / 119',
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: '50%',
      alignItems: 'stretch',
      justifyContent: 'center',
      padding: 32,
      gap: 48,
    },
  },

  textContentArea: {
    $base: {
      gap: 8,
    },
    $gteDesktop: {
      gap: 20,
    },
  },
  prefixImage: {
    $base: {
      height: 32,
    },
    $gteDesktop: {
      height: 44,
    },
  },
  prefixText: {
    $base: {
      color: tokens.colors.$houseDark,
      textTransform: 'uppercase',
      textAlign: 'center',
    },
  },
  buttons: {
    $base: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
  },
});
